// dito ASK — Teilnahme (ask.dito-ev.de): Start, Seiten rendern, speichern, abschließen. Vanilla ES-Modul, keine Abhängigkeiten.
// Antworten werden je Seite serverseitig validiert und gespeichert; der Client hält nur den aktuellen Stand.
import { evaluateRule } from './rules.js';
import { translator, DIR } from './i18n.js';
import { stageIcon } from './stage-icons.js';
import { companion, badge } from './companion.js';
import { isRich, renderRich } from './richtext.js';
const boot = JSON.parse(document.getElementById('ask-participation').textContent);
const root = document.getElementById('survey');
const storeKey = `ask:${boot.mode}:${boot.value}`;
const PREVIEW = boot.mode === 'preview';
const API_BASE = PREVIEW ? `/api/v1/surveys/${boot.survey_uid}/versions/${boot.version_no}/preview` : '/api/v1/participate';

// ---- Hilfen ----------------------------------------------------------------
function h(tag, ...rest) {
  const [name, ...classes] = tag.split('.');
  const el = document.createElement(name || 'div');
  if (classes.length) el.className = classes.join(' ');
  let attrs = {}, children = rest;
  if (rest.length && rest[0] !== null && typeof rest[0] === 'object' && !(rest[0] instanceof Node) && !Array.isArray(rest[0])) { attrs = rest[0]; children = rest.slice(1); }
  for (const [k, v] of Object.entries(attrs)) {
    if (v === undefined || v === null || v === false) continue;
    if (k.startsWith('on')) el.addEventListener(k.slice(2).toLowerCase(), v);
    else if (k === 'class') el.className = v;
    else if (k in el && typeof v !== 'object' && k !== 'list') { try { el[k] = v; } catch (e) { el.setAttribute(k, v); } }
    else el.setAttribute(k, v === true ? '' : String(v));
  }
  for (const c of children.flat(Infinity)) { if (c !== null && c !== undefined && c !== false) el.append(c instanceof Node ? c : document.createTextNode(String(c))); }
  return el;
}
function clear(el) { while (el.firstChild) el.removeChild(el.firstChild); return el; }
/** Kinder anhängen; null/undefined/false werden übersprungen (Element.append würde „null“ als Text einfügen). */
function add(el, ...children) { for (const c of children.flat(Infinity)) { if (c !== null && c !== undefined && c !== false) el.append(c instanceof Node ? c : document.createTextNode(String(c))); } return el; }
function uuid() { return (crypto.randomUUID ? crypto.randomUUID() : 'k-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12)); }
async function api(method, path, body) {
  const headers = { 'Accept': 'application/json' };
  if (body !== undefined) headers['Content-Type'] = 'application/json';
  if (PREVIEW && boot.csrf_token) headers['X-CSRF-Token'] = boot.csrf_token;
  let res;
  // Sprint 6 (Prüfung): ohne Antwort nach 20 s abbrechen – sonst bliebe „Weiter“ bei hängender Verbindung gesperrt;
  // der Abbruch zählt als Netzwerkfehler und wird mit demselben Schlüssel wiederholt (apiRetry)
  const ctl = typeof AbortController === 'function' ? new AbortController() : null;
  const timer = ctl ? setTimeout(() => ctl.abort(), 20000) : null;
  try {
    res = await fetch(API_BASE + path, { method, headers, credentials: 'same-origin', body: body === undefined ? undefined : JSON.stringify(body), signal: ctl ? ctl.signal : undefined });
  } catch (e) {
    // Sprint 6h: Netzwerkfehler (Funkloch, WLAN der Schule) wie „Server beschäftigt“ behandeln – wiederholbar
    const err = new Error(t('e_busy')); err.status = 0; err.code = 'network'; err.details = []; throw err;
  } finally { if (timer) clearTimeout(timer); }
  let data = null;
  try { data = res.status === 204 ? null : await res.json(); } catch (e) { data = null; }
  if (!res.ok) { const err = new Error(res.status === 503 ? t('e_busy') : ((data && data.error && data.error.message) || `Fehler ${res.status}`)); err.status = res.status; err.code = data?.error?.code || 'error'; err.details = data?.error?.details || []; throw err; }
  return data ? data.data : null;
}
/**
 * Sprint 6h (Lasttest/Betriebsprofil): Speichern und Abschließen bei kurzer Überlast (503) oder Netzwerkfehler bis zu
 * zweimal selbst wiederholen – mit DEMSELBEN Idempotenzschlüssel, damit eine doch angekommene Anfrage nicht doppelt
 * zählt (der Server antwortet dann mit der gespeicherten Antwort). Danach Meldung; erneutes Tippen auf „Weiter“ geht.
 */
async function apiRetry(method, path, body) {
  const waits = [1500, 3000];
  for (let i = 0; ; i++) {
    try { return await api(method, path, body); } catch (e) {
      if ((e.status === 503 || e.status === 0) && i < waits.length) { await new Promise(r => setTimeout(r, waits[i] * (0.8 + Math.random() * 0.4))); continue; }
      throw e;
    }
  }
}
/** Vorschau: Sprache und Motivationsprofil aus dem Aufruf (?lang=&audience=&motivation_level=&address_form=&sound=). */
function previewQuery() {
  const q = new URLSearchParams();
  if (boot.lang) q.set('lang', boot.lang);
  for (const [k, v] of Object.entries(boot.profile || {})) if (v) q.set(k, v);
  const str = q.toString();
  return str ? `?${str}` : '';
}
// Meilensteine je Teilnahme nur einmal zeigen (auch nach Zurück → Weiter); Merkliste je Teilnahme im sessionStorage
const msKey = () => `${storeKey}:ms:${(state && state.response_uid) || 'preview'}`;
function milestoneShown(i) { try { return (JSON.parse(sessionStorage.getItem(msKey()) || '[]')).includes(i); } catch (e) { return false; } }
function markMilestone(i) { try { const l = JSON.parse(sessionStorage.getItem(msKey()) || '[]'); if (!l.includes(i)) l.push(i); sessionStorage.setItem(msKey(), JSON.stringify(l)); } catch (e) {} }
const remember = (uid) => { try { sessionStorage.setItem(storeKey, uid); } catch (e) {} };
const remembered = () => { try { return sessionStorage.getItem(storeKey); } catch (e) { return null; } };
const forget = () => { try { sessionStorage.removeItem(storeKey); } catch (e) {} };

// ---- Zustand ----------------------------------------------------------------
let state = null;          // Antwort des Servers (survey, answers, client_revision …)
let pageIndex = 0;
let local = {};            // lokal geänderte Antworten je question_key (bis zum Speichern)

// ---- Sprache (Sprint 4d): Inhalte kommen übersetzt vom Server, Bedienelemente aus i18n.js; fa/ar rechts nach links ----
let lang = 'de';
let addressForm = 'sie';
let t = translator('de');
function setLanguage(code, form) {
  lang = code || 'de';
  if (form) addressForm = form;
  t = translator(lang, addressForm);
  document.documentElement.lang = lang;
  document.documentElement.dir = DIR[lang] || 'ltr';
}

// ---- Motivierende Teilnahme (Sprint 4h, docs/MOTIVATION_DESIGN.md) ---------------------------------------------
// Profil (Zielgruppe, Stufe, Anrede, Ton) und Etappen kommen vom Server (Version + Durchlauf); hier nur Darstellung:
// Stufe plain = Seitenzähler · friendly = Etappenleiste, Meilensteinseiten, Dauer, Bilanz · journey = zusätzlich
// Begleitfigur, Reisepfad und Abzeichen. Nichts davon berührt Fragen, Antwortformate oder Reihenfolge.
const FALLBACK_PROFILE = { audience: 'teachers', level: 'plain', address_form: 'sie', sound: false, milestones: true, badge: 'plaque' };
function profile() { return (state && state.survey && state.survey.motivation) || FALLBACK_PROFILE; }
function stages() { return (state && state.survey && Array.isArray(state.survey.stages)) ? state.survey.stages : []; }
function formal(p = profile()) { return p.audience === 'teachers' || p.audience === 'parents'; }
/** Text mit formeller Variante (Abschnitt statt Etappe) für Lehrkräfte/Eltern, wenn ein Schlüssel „…_formal“ existiert. */
function tw(key, vars = {}) { if (formal()) { const f = key + '_formal'; const v = t(f, vars); if (v !== f) return v; } return t(key, vars); }
/** Etappenfarbe (Nachtrag 4t) als CSS-Variable per CSSOM (CSP: keine Inline-Styles); ohne Farbe gilt die Themenfarbe. */
function stageColored(el, sg) { if (sg && sg.color) el.style.setProperty('--stage-color', sg.color); return el; }
function stageIndexOf(pageKey) { const sg = stages(); for (let i = 0; i < sg.length; i++) if (sg[i].page_keys.includes(pageKey)) return i; return -1; }
/** Etappenleiste statt Seitenzähler: ab Stufe „freundlich“, wenn Etappen definiert sind. */
function journeyActive() { return profile().level !== 'plain' && stages().length > 0; }
function durationLine(minutes) { return minutes === 1 ? t('m_duration_one') : t('m_duration', { n: minutes }); }
function pagesVisible() { const a = allAnswers(); return state.survey.pages.filter(p => pageVisible(p, a)); }
const NO_ANSWER_TYPES = ['info', 'divider', 'image']; // Elemente ohne Antwort (Informationstext, Trennlinie, Bild – Fix 3)
const isAnswerable = (q) => !NO_ANSWER_TYPES.includes(q.question_type);
function questionsSeen() { const a = allAnswers(); let n = 0; for (const p of pagesVisible()) for (const q of p.questions) if (isAnswerable(q) && questionVisible(q, p, a)) n++; return n; }
/** Sprint 5i: tatsächlich beantwortete Fragen (für „{q} Fragen beantwortet“ – freiwillig ausgelassene zählen nicht). */
function questionsAnswered() { const a = allAnswers(); let n = 0; for (const p of pagesVisible()) for (const q of p.questions) if (isAnswerable(q) && questionVisible(q, p, a) && a[q.question_key] != null) n++; return n; }

/** Etappenleiste (+ Abzeichenleiste in Stufe „Reise“) für die Seite idx; in Meilensteinseiten für die kommende Etappe. */
function journeyBar(idx) {
  const sg = stages(); const page = state.survey.pages[idx]; const cur = Math.max(0, stageIndexOf(page.page_key));
  const p = profile();
  const wrap = h('div.journey', { class: 'journey journey--' + p.level });
  if (p.level === 'journey') {
    wrap.append(h('div.badges', { role: 'list', 'aria-label': t('m_badges') }, h('span.badges__label', { 'aria-hidden': 'true' }, t('m_badges')),
      sg.map((x, i) => stageColored(h('span.badges__item', { role: 'listitem', title: x.title, 'aria-label': `${x.title}: ${i < cur ? '✓' : ''}` }, badge(p.badge, i < cur ? 'done' : i === cur ? 'active' : 'todo', i + 1)), x))));
  }
  const inStage = sg[cur] ? sg[cur].page_keys : [page.page_key];
  const pos = Math.max(0, inStage.indexOf(page.page_key));
  const valueText = tw('m_stage_of', { n: cur + 1, m: sg.length }) + (sg[cur] ? `: ${sg[cur].title}` : '') + (inStage.length > 1 ? ` – ${tw('m_stage_pages', { n: pos + 1, m: inStage.length })}` : '');
  const list = h('ol.stages', { class: 'stages' + (sg.length > 12 ? ' stages--dense' : sg.length > 7 ? ' stages--many' : ''), role: 'progressbar', 'aria-valuemin': 1, 'aria-valuemax': sg.length, 'aria-valuenow': cur + 1, 'aria-valuetext': valueText, 'aria-label': t('progress') },
    sg.map((x, i) => stageColored(h('li.stage', { class: 'stage' + (i < cur ? ' stage--done' : i === cur ? ' stage--active' : ''), 'aria-hidden': 'true' },
      h('span.stage__dot', i < cur ? stageIcon('check') : stageIcon(x.icon)),
      h('span.stage__label', x.title)), x)));
  wrap.append(list, h('p.stage__pos', { 'aria-hidden': 'true' }, h('span', tw('m_stage_of', { n: cur + 1, m: sg.length })), sg[cur] ? h('span.stage__pos-name', ` · ${sg[cur].title}`) : null, inStage.length > 1 ? h('span.stage__pos-page', ` · ${pos + 1}/${inStage.length}`) : null));
  return wrap;
}

/** Ablauf auf der Startseite: Dauer, „keine richtigen oder falschen Antworten“, Etappenliste mit Icons. */
function overviewBlock(minutes) {
  const p = profile(); const sg = stages();
  if (p.level === 'plain') return null;
  return h('div.overview',
    h('p.meta-line', h('span.meta-line__item', stageIcon('clock', 'sicon sicon--inline'), durationLine(minutes)), h('span.meta-line__item', stageIcon('smile', 'sicon sicon--inline'), t('m_no_wrong'))),
    sg.length ? h('div.stage-list', h('p.stage-list__title', t('m_overview')), h('ol.stage-list__items', sg.map(x => stageColored(h('li', h('span.stage-list__dot', stageIcon(x.icon)), h('span', x.title)), x)))) : null);
}

/**
 * Gestaltungselemente der Start- bzw. Abschlussseite (Nachtrag 4u): Informationstexte, Trennlinien, Bilder wie auf
 * Fragebogenseiten (inkl. Abstände, aufklappbarer Teil); null ohne Elemente. page = info.start_page / survey.end_page.
 */
function specialElements(page) {
  if (!page || !page.questions || !page.questions.length) return null;
  const box = h('div.special-elements', { 'data-special': page.kind || '' });
  for (const q of page.questions) {
    const el = renderQuestion(q, null, '');
    box.append(el);
    applySpacing(el, q, q, box);
  }
  return box;
}

/** Begleitfigur neben einer Überschrift (nur Stufe „Reise“; Start-, Meilenstein- und Dankesseite). */
function hero(pose, ...content) {
  if (profile().level !== 'journey') return h('div.hero.hero--plain', ...content);
  return h('div.hero', h('div.hero__figure', companion(pose)), h('div.hero__body', ...content));
}

let audioCtx = null;
/** Kurzer Klang bei Meilenstein und Abschluss – nur wenn der Durchlauf Ton erlaubt (Opt-in), per WebAudio ohne Dateien. */
function chime(kind) {
  if (!profile().sound) return;
  try {
    audioCtx = audioCtx || new (window.AudioContext || window.webkitAudioContext)();
    if (audioCtx.state === 'suspended') audioCtx.resume();
    const now = audioCtx.currentTime;
    const notes = kind === 'final' ? [523.25, 659.25, 783.99, 1046.5] : [659.25, 880];
    notes.forEach((f, i) => {
      const o = audioCtx.createOscillator(); const g = audioCtx.createGain(); const at = now + i * 0.13;
      o.type = 'sine'; o.frequency.value = f;
      g.gain.setValueAtTime(0.0001, at); g.gain.exponentialRampToValueAtTime(0.16, at + 0.02); g.gain.exponentialRampToValueAtTime(0.0001, at + 0.38);
      o.connect(g).connect(audioCtx.destination); o.start(at); o.stop(at + 0.4);
    });
  } catch (e) { /* kein Ton verfügbar */ }
}

/** Meilensteinseite zwischen zwei Etappen (keine Frageseite, zählt nicht im Fortschritt). pageIndex zeigt bereits auf die erste Seite der neuen Etappe. */
function renderMilestone(from, to) {
  const sg = stages(); const p = profile(); const done = sg[from]; const next = sg[to];
  const text = done.milestone_text || t('ms_' + p.audience, { n: from + 1 });
  const half = sg.length >= 4 && from + 1 === Math.floor(sg.length / 2) ? t('m_half') : null;
  const nextLine = to === sg.length - 1 ? tw('m_last_stage') : t('m_next_stage', { t: next.title });
  const pose = formal(p) ? 'thumbs' : 'wave';
  const body = [h('h1.milestone__title', tw('m_milestone_title', { n: from + 1 })), h('p.milestone__text', text, half ? [' ', half] : null), h('p.milestone__next', nextLine)];
  const figure = p.level === 'journey'
    ? h('div.hero.hero--milestone', h('div.hero__figure', companion(pose)), h('div.hero__body', h('div.bubble', body[1]), body[0], body[2]))
    : h('div.milestone__icon-wrap', h('span.milestone__icon', stageIcon(done.icon)), ...body);
  const earned = p.level === 'journey' ? h('p.milestone__badge', badge(p.badge, 'done', from + 1, 'badge--big'), h('span', t('m_badge_earned', { t: done.title }))) : h('span.milestone__check', stageIcon('check'));
  // Nachtrag 4t: Titelbild der Meilensteinseite (je Etappe) und Etappenfarbe für Icon/Abzeichen/Haken
  const card = stageColored(h('section.card.card--survey.card--milestone', designHeader(false, { banner_key: done.banner_key || 'none' }), !PREVIEW && state.is_test ? h('p.testbadge', t('test_badge')) : null,
    journeyActive() && (state.survey.settings || {}).show_progress !== false ? journeyBar(pageIndex) : null,
    figure, earned,
    h('div.survey-nav.survey-nav--center', h('button.btn.btn--primary', { type: 'button', onClick: () => renderPage() }, t('m_continue')))), done);
  render(card);
  chime('milestone');
}
/** Serverseitige Prüfmeldungen (deutsch) in der gewählten Sprache: nach Fehlercode und Fragetyp. */
function localizeError(d, q) {
  if (lang === 'de') return d.message;
  const type = q ? q.question_type : '';
  switch (d.code) {
    case 'required': return t(type === 'ranking' ? 'e_rank_required' : ['matrix_single', 'matrix_multi', 'matrix_dropdown'].includes(type) ? 'e_rows_required' : 'e_required');
    case 'too_long': return t('e_too_long');
    case 'invalid': return t(type === 'email' ? 'e_invalid_email' : 'e_invalid');
    case 'code_part': return t('e_code_part');
    case 'code_incomplete': return t('e_code_incomplete');
    case 'range': return t('e_range');
    case 'reserved': return t('e_reserved');
    case 'min_selected': return t('e_min_selected');
    case 'max_selected': return t('e_max_selected');
    case 'exclusive': return t('e_exclusive');
    default: return d.message;
  }
}

// ---- Erscheinungsbild (Sprint 4b): Farben/Schrift als CSS-Variablen (CSSOM, keine style-Attribute wegen CSP), Titelbild ----
let design = null;
function applyDesign(d) {
  if (!d) return;
  design = d;
  const r = document.documentElement.style;
  r.setProperty('--color-primary', d.primary); r.setProperty('--color-primary-contrast', d.primary_contrast); r.setProperty('--color-primary-hover', d.primary_hover);
  r.setProperty('--color-bg', d.background); r.setProperty('--color-text', d.text);
  r.setProperty('--design-header', d.header); r.setProperty('--design-header-contrast', d.header_contrast);
  r.setProperty('--font', d.font_stack);
  // Schriftgrößen (Nachtrag 4r): px je Element, leer = Standard der Stylesheets
  const FS = { block: '--fs-block', title: '--fs-title', instruction: '--fs-instruction', item: '--fs-item', option: '--fs-option', emoji: '--fs-emoji', item_emoji: '--fs-item-emoji' };
  for (const [k, v] of Object.entries(FS)) { const px = d.font_sizes && d.font_sizes[k]; if (px) r.setProperty(v, px + 'px'); else r.removeProperty(v); }
  document.body.classList.toggle('themed', !d.is_default);
}
/** Schriftgrößen einer Frage (Antwortskala/Frage überschreiben das Erscheinungsbild, Nachtrag 4r) per CSSOM am Element. */
function applyQuestionFontSizes(el, q) {
  if (!el || !q) return;
  if (q.option_font_px) el.style.setProperty('--fs-option', q.option_font_px + 'px');
  if (q.emoji_font_px) el.style.setProperty('--fs-emoji', q.emoji_font_px + 'px');
}
/**
 * Kopf mit Titelbild bzw. Farbleiste. Ohne Seite: Standard-Titelbild (withImage = Startseite/erste Seite) wie bisher.
 * Mit Seite (Nachtrag 4r): page.banner_key 'none' = kein Bild (nur Farbleiste), 'default' = Standard-Titelbild,
 * 'b<n>' = zusätzliches Titelbild aus design.banners, sonst wie bisher (Standardbild nur auf der ersten Seite).
 */
/** Sprint 7d: Logo der Umfrage (Position links/mittig/rechts, Höhe in px per CSSOM); „nur Startseite“ über where = 'start'. */
function logoNode(where = '') {
  const l = design && design.logo;
  if (!l || (l.pages === 'start' && where !== 'start')) return null;
  const img = h('img.survey-logo__img', { src: l.url, alt: l.alt || '', width: l.width, height: l.height, decoding: 'async' });
  img.style.setProperty('height', l.height_px + 'px');
  return h('div.survey-logo', { class: 'survey-logo survey-logo--' + (l.position || 'left') }, img);
}
function designHeader(withImage, page = null, where = '') {
  if (!design) return null;
  return [bannerNode(withImage, page), logoNode(where)];
}
function bannerNode(withImage, page) {
  const key = page && page.banner_key ? page.banner_key : null;
  let img = null;
  if (key === 'none') img = null;
  else if (key === 'default') img = design.banner || null;
  else if (key) img = (design.banners || []).find(b => b.key === key) || (withImage ? design.banner : null);
  else if (withImage) img = design.banner || null;
  if (img) return h('div.banner', { 'data-banner': key || 'default' }, h('img.banner__img', { src: img.url, alt: img.alt || '', width: img.width, height: img.height, decoding: 'async' }));
  return design.is_default ? null : h('div.banner.banner--bar', { 'aria-hidden': 'true' });
}
/** Link „Datenschutz“ im Fuß (Nachtrag 4r): je Umfrageversion einstellbar; öffnet immer in neuem Tab. */
function applyPrivacyLink(url) {
  const a = document.querySelector('.footer a[data-privacy], .footer a');
  if (!a) return;
  if (url && /^https:\/\//.test(url)) a.href = url;
  a.target = '_blank'; a.rel = 'noopener noreferrer';
}

function render(node) { clear(root); root.append(node); root.setAttribute('aria-busy', 'false'); const hd = root.querySelector('h1, h2'); if (hd) { hd.setAttribute('tabindex', '-1'); hd.focus({ preventScroll: true }); } window.scrollTo(0, 0); }

function messageScreen(title, text, kind = 'info') {
  return h('section.card.card--survey', h('h1', title), h('p', { class: kind === 'error' ? 'error' : '' }, text));
}

const CLOSED_CODES = ['run_not_started', 'run_paused', 'run_closed', 'token_used', 'token_expired', 'token_invalid', 'not_found', 'resume_invalid'];
function closedScreen(code, fallback) { const [title, m] = CLOSED_CODES.includes(code) ? t('c_' + code) : [t('h_notice'), fallback || t('h_unavailable')]; return messageScreen(title, m); }

// ---- Start --------------------------------------------------------------------
async function init() {
  if (PREVIEW && boot.measure) { await measureAll(); return; }
  if (PREVIEW) {
    state = await api('GET', previewQuery());
    local = {}; pageIndex = 0;
    // Nachtrag 4s: jeder Vorschaulauf zeigt die Meilensteinseiten erneut (die Merkliste gilt je Teilnahme, nicht je Tab)
    try { sessionStorage.removeItem(msKey()); } catch (e) { /* kein Speicher */ }
    setLanguage(state.survey.language, state.survey.motivation?.address_form);
    applyDesign(state.survey.design);
    applyPrivacyLink(state.survey.settings && state.survey.settings.privacy_url);
    render(h('section.card.card--survey.card--start', designHeader(true, state.survey.start_page, 'start'), h('p.testbadge', t('preview_badge')),
      hero('wave', h('h1', state.survey.title), overviewBlock(state.survey.estimated_minutes || 1)),
      state.survey.intro_text ? h('div.intro.rt', ...richOrText(state.survey.intro_text, 2)) : null,
      specialElements(state.survey.start_page),
      h('button.btn.btn--primary.btn--big', { type: 'button', onClick: () => renderPage() }, t('preview_start'))));
    return;
  }
  let info;
  try { info = await api('GET', `/info?${boot.mode}=${encodeURIComponent(boot.value)}`); }
  catch (e) { render(closedScreen(e.code, e.message)); return; }
  setLanguage(info.language, info.motivation?.address_form);
  applyDesign(info.design);
  applyPrivacyLink(info.privacy_url);
  if (!info.open) { render(closedScreen(info.reason)); return; }
  const prev = remembered();
  if (prev) {
    try { state = await api('GET', `/${prev}`); if (state.status === 'completed') { forget(); } else { setLanguage(state.survey.language, state.survey.motivation?.address_form); applyDesign(state.survey.design); applyPrivacyLink(state.survey.settings && state.survey.settings.privacy_url); pageIndex = Math.max(0, state.survey.pages.findIndex(p => p.page_key === state.current_page_key)); renderPage(); return; } }
    catch (e) { forget(); }
  }
  renderStart(info);
}

/** Sprachwahl auf der Startseite (nur wenn die Version/der Durchlauf sie erlaubt): Umschalten lädt Titel/Begrüßung neu. */
function languageSwitcher(info) {
  if (!info.language_choice || !info.languages || info.languages.length < 2) return null;
  return h('div.langs', { role: 'group', 'aria-label': t('language') },
    info.languages.map(l => h('button.btn.btn--small.lang', { type: 'button', lang: l.code, dir: l.dir, class: 'btn btn--small lang' + (l.code === lang ? ' lang--active' : ''), 'aria-pressed': l.code === lang ? 'true' : 'false', onClick: async () => {
      if (l.code === lang) return;
      try { const next = await api('GET', `/info?${boot.mode}=${encodeURIComponent(boot.value)}&lang=${encodeURIComponent(l.code)}`); setLanguage(next.language); renderStart(next); }
      catch (e) { render(closedScreen(e.code, e.message)); }
    } }, l.name_native)));
}

function renderStart(info) {
  const errorEl = h('p.error', { role: 'alert', hidden: true });
  const codeField = info.needs_code ? h('div.field', h('label', { for: 'code' }, t('code_label')), h('input', { id: 'code', name: 'code', type: 'text', autocomplete: 'off', autocapitalize: 'characters', spellcheck: false, inputmode: 'text', placeholder: t('code_placeholder') }), h('p.field-error', { 'data-error-for': 'code', hidden: true })) : null;
  const consent = info.consent_required ? h('div.field.field--check', h('label', h('input', { type: 'checkbox', id: 'consent' }), ' ' + t('consent_label'))) : null;
  const form = h('form', { novalidate: true, onSubmit: async (ev) => {
    ev.preventDefault();
    errorEl.hidden = true;
    if (consent && !consent.querySelector('input').checked) { errorEl.textContent = t('consent_first'); errorEl.hidden = false; return; }
    const btn = form.querySelector('button[type=submit]'); btn.disabled = true;
    const body = boot.mode === 'token' ? { token: boot.value } : { slug: boot.value };
    if (codeField) body.code = codeField.querySelector('input').value;
    body.language = lang;
    try {
      state = await api('POST', '/start', body);
      setLanguage(state.survey.language, state.survey.motivation?.address_form);
      applyDesign(state.survey.design);
      applyPrivacyLink(state.survey.settings && state.survey.settings.privacy_url);
      remember(state.response_uid);
      local = {};
      pageIndex = Math.max(0, state.survey.pages.findIndex(p => p.page_key === state.current_page_key));
      renderPage();
    } catch (e) {
      btn.disabled = false;
      if (e.details && e.details.length && codeField) { const p = codeField.querySelector('.field-error'); p.textContent = e.details[0].message; p.hidden = false; codeField.querySelector('input').focus(); return; }
      if (e.status === 410 || e.status === 404) { render(closedScreen(e.code, e.message)); return; }
      errorEl.textContent = e.message; errorEl.hidden = false;
    }
  } },
    info.intro_text ? h('div.intro.rt', ...richOrText(info.intro_text, 2)) : null,
    specialElements(info.start_page),   // Nachtrag 4u: gestaltbare Startseite
    info.privacy_text ? h('details.privacy', { open: info.consent_required }, h('summary', t('privacy_summary')), ...paragraphs(info.privacy_text)) : null,
    codeField, consent, errorEl,
    h('button.btn.btn--primary.btn--big', { type: 'submit' }, t('start')));
  // Startseite im Profil des Durchlaufs (Sprint 4h): Figur, Dauer, Etappen – vor dem Start gibt es noch keinen state
  const startProfile = info.motivation || FALLBACK_PROFILE;
  const saved = state; state = { survey: { motivation: startProfile, stages: info.stages || [], pages: [] } };
  const head = hero('wave', h('h1', info.title), overviewBlock(info.estimated_minutes || 1));
  state = saved;
  render(h('section.card.card--survey.card--start', designHeader(true, info.start_page, 'start'), languageSwitcher(info), info.is_test ? h('p.testbadge', t('test_badge_start')) : null, head, form));
}

function paragraphs(text) { return String(text).split(/\n{2,}/).map(t => h('p', ...t.split('\n').flatMap((l, i) => i ? [h('br'), l] : [l]))); }
/** Rich-Text (Nachtrag 4i) oder reiner Text als Knoten; level = HTML-Ebene für „Überschrift 1“ (unter dem Seitentitel 3). */
/** Aufklappbarer unterer Teil eines Informationstexts (Nachtrag 4t): Pfeil + „Mehr anzeigen“, Zustand nur im Browser. */
function infoMore(q) {
  if (!q.text_more) return null;
  const body = h('div.q-info.q-info__more.rt', { id: `${q.question_key}-more`, hidden: true }, ...richOrText(q.text_more, 3));
  const label = h('span', t('more_show'));
  const btn = h('button.q-info__toggle', { type: 'button', 'aria-expanded': 'false', 'aria-controls': `${q.question_key}-more`, onClick: () => {
    const open = body.hidden; body.hidden = !open; btn.setAttribute('aria-expanded', open ? 'true' : 'false'); btn.classList.toggle('q-info__toggle--open', open); label.textContent = t(open ? 'more_hide' : 'more_show');
  } }, h('span.q-info__chev', { 'aria-hidden': 'true' }, '▾'), label);
  return h('div.q-info-more', btn, body);
}
function richOrText(text, level = 3) { return isRich(text) ? renderRich(text, { headingLevel: level }) : paragraphs(text); }
/** Wirksamer Instruktionsmodus einer Gruppe: Gruppe > Version > „an jedem Item“ (ältere Versionen ohne Einstellung). */
function instructionMode(g, settings) { return (g && g.instruction_mode) || settings.instruction_mode || 'item'; }

// ---- Seiten -------------------------------------------------------------------
function answersOf(key) { return local[key] !== undefined ? local[key] : (state.answers[key] ?? null); }
function allAnswers() { const out = { ...state.answers }; for (const [k, v] of Object.entries(local)) { if (v === null) delete out[k]; else out[k] = v; } return out; }
function questionIndex() { const idx = {}; for (const p of state.survey.pages) for (const q of p.questions) idx[q.question_key] = q; return idx; }
function pageVisible(page, answers) { return !page.rule || evaluateRule(page.rule, answers, questionIndex()); }
function questionVisible(q, page, answers) { return pageVisible(page, answers) && (!q.rule || evaluateRule(q.rule, answers, questionIndex())); }

/**
 * Baut die Karte einer Fragebogenseite. opts.measure: alle Fragen zeigen (Regeln ignorieren), keine Testkennzeichnung –
 * für die Messansicht („Für Handy aufteilen“); opts.forceOther: Farbleiste statt Titelbild (Kopf einer Folgeseite).
 */
function buildPageCard(idx, opts = {}) {
  const s = state.survey; const settings = s.settings || {};
  const measure = !!opts.measure;
  const pages = s.pages; const page = pages[idx];
  const answersNow = allAnswers();
  const isLast = measure ? false : !pages.slice(idx + 1).some(p => pageVisible(p, answersNow));
  let number = settings.number_questions ? pages.slice(0, idx).reduce((n, p) => n + p.questions.filter(isAnswerable).length, 0) : 0;
  const form = h('form.survey-page', { novalidate: true });
  const errorSummary = h('div.error', { role: 'alert', hidden: true, tabindex: '-1' });
  form.append(errorSummary);
  // Teilseite einer aufgeteilten Seite („Titel (2/3)“): Titel nicht wiederholen – der Fortschritt zeigt die Position
  const baseTitle = (t) => String(t || '').replace(/\s*\(\d+\/\d+\)$/, '');
  const continuation = idx > 0 && page.title && baseTitle(page.title) === baseTitle(pages[idx - 1].title) && /\(\d+\/\d+\)$/.test(page.title);
  if (settings.show_page_titles !== false && page.title && !continuation) form.append(h('h2', { 'data-m': 'title' }, baseTitle(page.title)));
  if (page.description) form.append(h('div.page-desc.rt', { 'data-m': 'desc' }, ...richOrText(page.description, 3)));
  let currentGroup = null; let groupBox = null; let hideItemInstruction = false;
  let battery = null; // laufende Matrix-Batterie: { el, signature, group }
  for (const q of page.questions) {
    if (!measure && !questionVisible(q, page, answersNow)) { local[q.question_key] = null; continue; }
    if (isAnswerable(q)) number++;
    if (q.group_key !== currentGroup) {
      currentGroup = q.group_key; battery = null;
      const g = (page.groups || []).find(x => x.group_key === q.group_key);
      // Instruktionsmodus (Nachtrag 4i): „einmal je Skala“/„je Dimension“ → die Gruppeninstruktion steht einmal vor der
      // Batterie, die Item-Instruktionen aus dem Pool werden nicht wiederholt; „an jedem Item“ zeigt sie wie bisher
      hideItemInstruction = !!g && instructionMode(g, settings) !== 'item';
      groupBox = g ? h('fieldset.q-group', { 'data-group': g.group_key, 'data-instruction-mode': instructionMode(g, settings) }, h('legend.q-group__title', g.title), g.description ? h('div.q-inst.rt', ...richOrText(g.description, 3)) : null) : null;
      if (groupBox) form.append(groupBox);
    }
    const target = groupBox && q.group_key ? groupBox : form;
    const qq = hideItemInstruction && q.instruction ? { ...q, instruction: null } : q;
    const sig = batterySignature(q, autoBattery(q, page, pages));
    if (sig) {
      // Items mit Anordnung „Matrix“ und derselben Antwortskala werden als eine Tabelle gezeigt (Kopfzeile einmal, je Item eine Zeile)
      if (!battery || battery.signature !== sig) {
        // bis 7 Optionen: auf dem Smartphone kompakt (Optionen einmal als Spaltenkopf, je Item eine Zeile Auswahlkreise)
        battery = { signature: sig, el: h('div.q-matrix.q-battery', { role: 'group', class: 'q-matrix q-battery' + (q.options.length <= 7 ? ' q-battery--compact' : '') + (rowsCarryOptions(q) ? ' q-battery--rows' : '') }, matrixHead(q)) };
        battery.el.style.setProperty('--cols', String(q.options.length));
        applyQuestionFontSizes(battery.el, q);
        target.append(battery.el);
      }
      battery.el.append(renderQuestion(qq, settings.number_questions ? number : null, settings.required_marker ?? '*', 'row'));
      // Abstand (Fix 3): die Batterie übernimmt den Abstand davor vom ersten und danach vom zuletzt eingefügten Item
      applySpacing(battery.el, battery.el.querySelectorAll(':scope > .q').length === 1 ? q : null, q, target);
      continue;
    }
    battery = null;
    const el = renderQuestion(qq, settings.number_questions ? number : null, settings.required_marker ?? '*');
    target.append(el);
    applySpacing(el, q, q, target);
  }
  const prevVisible = measure ? idx - 1 : (() => { for (let i = idx - 1; i >= 0; i--) if (pageVisible(pages[i], answersNow)) return i; return -1; })();
  const back = (settings.allow_back !== false && prevVisible >= 0) ? h('button.btn', { type: 'button', onClick: () => { collect(page); pageIndex = prevVisible; renderPage(); } }, t('back')) : null;
  const next = h('button.btn.btn--primary', { type: 'submit' }, isLast ? t('finish') : t('next'));
  form.append(h('div.survey-nav', back, next));
  let progress = null;
  if (settings.show_progress !== false && journeyActive()) {
    progress = journeyBar(idx);
  } else if (settings.show_progress !== false) {
    const bar = h('div.progress__bar');
    bar.style.width = `${Math.round((idx / pages.length) * 100)}%`; // CSSOM statt style-Attribut (CSP)
    progress = h('div.progress', { role: 'progressbar', 'aria-valuemin': 0, 'aria-valuemax': pages.length, 'aria-valuenow': idx + 1, 'aria-label': t('progress') }, bar, h('span.progress__text', t('page_of', { n: idx + 1, m: pages.length })));
  }
  // Umfragetitel auf dem Smartphone nur auf der ersten Fragebogenseite (spart Höhe; Startseite zeigt ihn ohnehin)
  const later = opts.forceOther || idx > 0;
  const card = h('section.card.card--survey', designHeader(opts.forceOther ? false : idx === 0, page), !measure && state.is_test ? h('p.testbadge', t('test_badge')) : null, h('h1.survey-title', { class: 'survey-title' + (later ? ' survey-title--later' : '') }, s.title), progress, form);
  return { card, form, page, pages, next, back };
}

function renderPage() {
  const { card, form, page, pages, next, back } = buildPageCard(pageIndex);
  form.addEventListener('submit', async (ev) => {
    ev.preventDefault();
    collect(page);
    next.disabled = true; if (back) back.disabled = true;
    clearErrors(form);
    const answers = {};
    for (const q of page.questions) { const v = answersOf(q.question_key); if (v !== null && v !== undefined) answers[q.question_key] = v; }
    try {
      const r = PREVIEW
        ? await api('POST', `/pages/${page.page_key}`, { answers, all_answers: allAnswers() })
        : await apiRetry('PUT', `/${state.response_uid}/pages/${page.page_key}`, { client_revision: state.client_revision, idempotency_key: uuid(), answers });
      state.client_revision = r.client_revision;
      for (const q of page.questions) { if (local[q.question_key] !== undefined) { if (local[q.question_key] === null) delete state.answers[q.question_key]; else state.answers[q.question_key] = local[q.question_key]; delete local[q.question_key]; } }
      const nextIdx = r.next_page_key ? pages.findIndex(p => p.page_key === r.next_page_key) : -1;
      if (nextIdx < 0) {
        const c = PREVIEW ? await api('POST', '/complete', { all_answers: allAnswers() }) : await apiRetry('POST', `/${state.response_uid}/complete`, { client_revision: state.client_revision, idempotency_key: uuid() });
        state.client_revision = c.client_revision;
        forget();
        renderDone();
      } else {
        // Meilensteinseite beim Wechsel in eine spätere Etappe (Sprint 4h): nur vorwärts, nur mit Etappen und Stufe ≠ sachlich
        const from = stageIndexOf(page.page_key); const to = stageIndexOf(pages[nextIdx].page_key);
        pageIndex = nextIdx;
        if (journeyActive() && profile().milestones !== false && from >= 0 && to > from && !milestoneShown(from)) { markMilestone(from); renderMilestone(from, to); } else renderPage();
      }
    } catch (e) {
      next.disabled = false; if (back) back.disabled = false;
      if (e.status === 400 && e.details?.length) { showErrors(form, e.details, page); return; }
      if (e.status === 409 && e.code === 'revision_mismatch') { state = await api('GET', `/${state.response_uid}`); local = {}; renderPage(); return; }
      if (e.status === 409 && e.code === 'already_completed') { forget(); renderDone(); return; }
      if (PREVIEW && (e.status === 401 || e.status === 403)) { render(messageScreen(t('h_session'), t('h_session_text'), 'error')); return; }
      if (e.status === 410 || e.status === 403) { forget(); render(closedScreen(e.code, e.message)); return; }
      const summary = form.querySelector('[role=alert]');
      summary.textContent = e.message; summary.hidden = false; summary.focus();
    }
  });
  render(card);
}

// ---- Messansicht („Für Handy aufteilen“, nur Vorschau, im Editor als iframe in Gerätebreite) -------------------------
/**
 * Rendert alle Seiten untereinander im echten Layout und misst je Seite: Kopf (Titelbild/Leiste, Titel, Fortschritt),
 * Seitentitel, Beschreibung, Gruppenkopf/-fuß, jede Frage (Abstand bis zum nächsten Element) und die Navigation.
 * Außerdem Elemente, die seitlich über den Bildschirm hinausragen. Ergebnis: window.__askMeasure.
 */
async function measureAll() {
  state = await api('GET', previewQuery()); local = {};
  setLanguage(state.survey.language, state.survey.motivation?.address_form);
  applyDesign(state.survey.design);
  // Messung mit dem höchsten Kopf (Stufe „Reise“ mit Abzeichenleiste), damit die Aufteilung für jeden Durchlauf passt
  if (state.survey.motivation && stages().length) state.survey.motivation.level = 'journey';
  document.body.classList.add('measuring');
  const pages = state.survey.pages;
  const wrap = h('div.measure');
  const cards = pages.map((p, i) => { const b = buildPageCard(i, { measure: true }); wrap.append(b.card); return b; });
  const other = buildPageCard(0, { measure: true, forceOther: true });
  wrap.append(other.card);
  clear(root); root.append(wrap); root.setAttribute('aria-busy', 'false');
  await Promise.all([...wrap.querySelectorAll('img')].map(img => img.complete ? null : new Promise(r => { img.addEventListener('load', r); img.addEventListener('error', r); })));
  if (document.fonts && document.fonts.ready) { try { await document.fonts.ready; } catch (e) { /* egal */ } }
  await new Promise(r => requestAnimationFrame(() => requestAnimationFrame(r)));
  const docY = (el) => el.getBoundingClientRect().top + window.scrollY;
  const pageTop = docY(cards.length ? cards[0].card : other.card); // Abstand Dokumentanfang → Karte (Rand von main)
  const result = { width: window.innerWidth, chrome: {}, pages: {}, groups: {}, questions: {}, overflow: [] };
  const overflow = {};
  const contentStart = (b) => { const f = [...b.form.children].find(c => !c.hidden && c.getClientRects().length); return f ? docY(f) : docY(b.form); };
  const navOf = (b) => b.form.querySelector('.survey-nav');
  if (cards.length) result.chrome.top_first = Math.ceil(contentStart(cards[0]) - docY(cards[0].card) + pageTop);
  result.chrome.top_other = Math.ceil(contentStart(other) - docY(other.card) + pageTop);
  const nav = navOf(other); const nr = nav.getBoundingClientRect();
  result.chrome.bottom = Math.ceil(nr.height + 8);
  for (const b of cards) {
    const f = b.form;
    const title = f.querySelector(':scope > [data-m=title]'); const desc = f.querySelector(':scope > [data-m=desc]');
    // Folge-Elemente bestimmen: Höhe = Abstand zum nächsten Element im Fluss
    const flow = [...f.children].filter(c => !c.hidden && c.getClientRects().length);
    const nextTop = (el) => { const i = flow.indexOf(el); return i >= 0 && i + 1 < flow.length ? docY(flow[i + 1]) : docY(navOf(b)); };
    result.pages[b.page.page_key] = { title: title ? Math.ceil(nextTop(title) - docY(title)) : 0, description: desc ? Math.ceil(nextTop(desc) - docY(desc)) : 0 };
    const navTop = docY(navOf(b));
    // Marken in Dokumentreihenfolge: Gruppenanfang, Fragen (erste Batteriezeile ab Batterieanfang), Gruppenende
    const marks = [];
    for (const el of flow) {
      if (el === title || el === desc || el.classList.contains('survey-nav')) continue;
      if (el.matches('fieldset.q-group')) {
        const gk = el.dataset.group;
        const legend = el.querySelector(':scope > legend');
        marks.push({ t: 'gstart', k: gk, y: Math.min(docY(el), legend ? docY(legend) : Infinity) });
        const qs = [...el.querySelectorAll('[data-q]')];
        // Beginnt die Gruppe mit einer Batterie, gehört deren Spaltenkopf zum Gruppenkopf – er wird wie Titel und
        // Instruktion auf jedem Teil einer geteilten Batterie wiederholt.
        qs.forEach((q, i) => marks.push({ t: 'q', k: q.dataset.q, y: i === 0 && q.parentElement.classList.contains('q-battery') ? docY(q) : startOf(q) }));
        const last = qs[qs.length - 1];
        marks.push({ t: 'gend', k: gk, y: last ? last.getBoundingClientRect().bottom + window.scrollY : docY(el) });
      } else {
        for (const q of (el.matches('[data-q]') ? [el] : [...el.querySelectorAll('[data-q]')])) marks.push({ t: 'q', k: q.dataset.q, y: startOf(q) });
      }
    }
    marks.push({ t: 'end', y: navTop });
    for (let i = 0; i < marks.length - 1; i++) {
      const m = marks[i]; const hgt = Math.max(0, Math.ceil(marks[i + 1].y - m.y));
      if (m.t === 'q') result.questions[m.k] = hgt;
      else if (m.t === 'gstart') (result.groups[m.k] ||= { head: 0, foot: 0 }).head = hgt;
      else if (m.t === 'gend') (result.groups[m.k] ||= { head: 0, foot: 0 }).foot = hgt;
    }
    // Seitliches Überragen (seitliches Wischen) je Frage
    const right = document.documentElement.clientWidth;
    for (const el of b.card.querySelectorAll('*')) {
      if (el.classList.contains('sr-only') || el.closest('.sr-only')) continue;
      const r = el.getBoundingClientRect();
      if (!r.width) continue;
      const px = Math.max(r.right - right, -r.left);
      if (px > 1) { const q = el.closest('[data-q]'); const k = q ? q.dataset.q : '(Kopf)'; overflow[k] = Math.max(overflow[k] || 0, Math.ceil(px)); }
    }
  }
  result.overflow = Object.entries(overflow).map(([question_key, px]) => ({ question_key, px }));
  result.document_overflow = Math.max(0, document.documentElement.scrollWidth - document.documentElement.clientWidth);
  window.__askMeasure = result;
  document.documentElement.dataset.measure = 'done';
}
/** Beginn einer Frage im Fluss: erste Zeile einer Matrix-Batterie beginnt am Batterieanfang (Kopfzeile gehört dazu). */
function startOf(q) {
  const bat = q.parentElement && q.parentElement.classList.contains('q-battery') ? q.parentElement : null;
  const el = bat && bat.querySelector('[data-q]') === q ? bat : q;
  return el.getBoundingClientRect().top + window.scrollY;
}

function renderDone() {
  const settings = state.survey.settings || {};
  // Abschlussmeldung: übersetzt vom Server (Versionsübersetzung); unübersetzter deutscher Standardtext → Standard der Sprache/Anrede
  const cm = settings.completion_message;
  const isDefault = cm === 'Vielen Dank für Ihre Teilnahme.';
  const message = cm && !(isDefault && (lang !== 'de' || addressForm === 'du')) ? cm : t('thanks');
  const p = profile(); const sg = stages();
  let summary = null;
  if (p.level !== 'plain') {
    const q = questionsSeen();
    summary = h('div.finish',
      sg.length ? h('p.finish__line', tw('m_all_done')) : null,
      h('p.finish__summary', sg.length ? tw('m_summary', { s: sg.length, q }) : t('m_summary_questions', { q: questionsAnswered() })),
      p.level === 'journey' && sg.length ? h('div.badges.badges--final', { role: 'list', 'aria-label': t('m_badges') },
        sg.map((x, i) => stageColored(h('span.badges__item.badges__item--final', { role: 'listitem' }, badge(p.badge, 'done', i + 1, 'badge--big'), h('span.badges__name', x.title)), x)),
        h('span.badges__item.badges__item--final', { role: 'listitem' }, badge(p.badge, 'done', 'motif', 'badge--big'), h('span.badges__name', t('m_badge_final')))) : null);
  }
  // Nachtrag 4u: Abschlussseite mit Titelbild (nur wenn gewählt – ohne Wahl wie bisher ohne Kopf) und Gestaltungselementen
  const endPage = state.survey.end_page;
  render(h('section.card.card--survey.card--done', endPage && endPage.banner_key ? designHeader(false, endPage) : logoNode(), PREVIEW ? h('p.testbadge', t('preview_badge_done')) : null,
    hero('cheer', h('h1', message), summary),
    state.survey.outro_text ? h('div.rt', ...richOrText(state.survey.outro_text, 2)) : null,
    specialElements(endPage),
    PREVIEW ? h('p', h('button.btn', { type: 'button', onClick: () => { state.answers = {}; local = {}; pageIndex = 0; try { sessionStorage.removeItem(msKey()); } catch (e) {} renderPage(); } }, t('preview_again'))) : h('p.muted', t('close_window'))));
  if (p.level !== 'plain') chime('final');
}

/** Kennung der Antwortskala für Matrix-Batterien (nur Einfachauswahl/Bewertung mit Anordnung „Matrix“, ohne Freitext-Option). */
// Smartphone (< 640 px): Items einer Gruppe mit derselben Skala (bis 7 Optionen, Anordnung „automatisch“ oder „horizontal“)
// werden als kompakte Batterie gezeigt – Optionen einmal als Spaltenkopf, je Item eine Zeile Auswahlkreise. Bedingung:
// die Gruppe enthält auf dieser Seite mindestens zwei solche Items oder ist Teil einer aufgeteilten Batterie
// (continues_group_key, auch der erste Teil). Die Messung für „Für Handy aufteilen“ läuft in Handybreite und sieht
// dieselbe Darstellung. „vertikal“ bleibt eine Liste; am Desktop ändert sich nichts.
const PHONE_QUERY = '(max-width: 639px)';
/** Umbruchmöglichkeit nach „/“ (z. B. „teils/teils“) für schmale Spaltenköpfe, statt mitten im Wort zu brechen. */
function breakAfterSlash(t) { const parts = String(t).split('/'); return parts.flatMap((p, i) => i < parts.length - 1 ? [p + '/', h('wbr')] : [p]); }
function autoBattery(q, page, pages) {
  if (!(q.layout == null || q.layout === 'horizontal') || !q.group_key || q.options.length > 7) return false;
  if (!(window.matchMedia && window.matchMedia(PHONE_QUERY).matches)) return false;
  const sig = batterySignature(q, true);
  if (!sig) return false;
  const g = (page.groups || []).find(x => x.group_key === q.group_key);
  if (g && g.continues_group_key) return true;
  if ((pages || []).some(p => (p.groups || []).some(x => x.continues_group_key === q.group_key))) return true;
  return page.questions.filter(o => o.group_key === q.group_key && o.layout === q.layout && batterySignature(o, true) === sig).length >= 2;
}

function batterySignature(q, auto = false) {
  if (q.layout !== 'matrix' && !(auto && (q.layout == null || q.layout === 'horizontal'))) return null;
  if (q.question_type !== 'single_choice' && q.question_type !== 'rating') return null;
  if (q.display_style === 'dropdown' || q.display_style === 'slider') return null;
  if (q.options.some(o => o.has_other_text)) return null;
  return q.options.map(o => `${o.option_key}\u0001${o.label}\u0001${o.display_value || ''}`).join('\u0002') + '\u0003' + (q.display_style || '') + '\u0003' + (q.label_mode || 'text');
}

function clearErrors(form) { for (const p of form.querySelectorAll('.q-error')) { p.hidden = true; p.textContent = ''; } for (const q of form.querySelectorAll('.q--invalid')) q.classList.remove('q--invalid'); const s = form.querySelector('[role=alert]'); if (s) { s.hidden = true; s.textContent = ''; } }
function showErrors(form, details, page) {
  const summary = form.querySelector('[role=alert]');
  const items = [];
  let first = null;
  for (const d of details) {
    const box = form.querySelector(`[data-q="${CSS.escape(d.field)}"]`);
    const q = page.questions.find(x => x.question_key === d.field);
    const msg = localizeError(d, q);
    if (box) { box.classList.add('q--invalid'); const p = box.querySelector('.q-error'); p.textContent = msg; p.hidden = false; if (!first) first = box; }
    items.push(h('li', h('a', { href: '#q-' + d.field, onClick: (ev) => { ev.preventDefault(); box?.scrollIntoView({ block: 'center' }); box?.querySelector('input, select, textarea, button')?.focus(); } }, q ? q.text : d.field), ': ' + msg));
  }
  clear(summary); summary.append(h('p', details.length === 1 ? t('errors_one') : t('errors_many', { n: details.length })), h('ul', items)); summary.hidden = false; summary.focus();
}

/**
 * Abstand vor/nach einem Element (Fix 3, 14.10.2026): config.space_before / space_after in px, Standard 24 (= bisheriger
 * Abstand aus participate.css; in Gruppen kompakter). Nur abweichende Werte werden per CSSOM gesetzt (CSP: kein
 * style-Attribut). Vertikale Außenabstände benachbarter Elemente fallen zusammen – der Abstand ist der größere der
 * beiden Werte. Vor dem ersten Element eines Bereichs wirkt „davor“ nur, wenn es ausdrücklich gesetzt ist.
 */
const SPACING_DEFAULT = 24;
function applySpacing(el, beforeQ, afterQ, container) {
  if (beforeQ) {
    const b = beforeQ.config && beforeQ.config.space_before;
    const first = container && container.querySelector(':scope > .q, :scope > .q-battery') === el;
    if (b !== undefined && b !== null && b !== '' && (Number(b) !== SPACING_DEFAULT || first)) el.style.marginTop = `${Number(b)}px`;
    else if (!first) el.style.removeProperty('margin-top');
  }
  if (afterQ) {
    const a = afterQ.config && afterQ.config.space_after;
    if (a !== undefined && a !== null && a !== '' && Number(a) !== SPACING_DEFAULT) el.style.marginBottom = `${Number(a)}px`;
    else el.style.removeProperty('margin-bottom');
  }
}

// ---- Fragen rendern ---------------------------------------------------------------
function renderQuestion(q, number, marker, mode = 'block') {
  const id = 'q-' + q.question_key;
  const a = (NO_ANSWER_TYPES.includes(q.question_type) ? null : answersOf(q.question_key)) || {};
  const label = [number ? h('span.q-no', `${number}. `) : null, q.text, q.is_required ? h('span.req', { 'aria-hidden': 'true' }, ' ' + marker) : null, q.is_required ? h('span.sr-only', ' ' + t('required_sr')) : null];
  const wrap = h('div.q', { 'data-q': q.question_key, id, class: mode === 'row' ? 'q q--row' : 'q' });
  applyQuestionFontSizes(wrap, q);
  // Erkennungsemoji (Sprint 4d): Einzelfrage → zwei Spalten (schmale Emoji-Spalte, dann der ganze Inhalt);
  // Batteriezeile → Emoji vor dem Zeilentext in der Beschriftungszelle
  const emoji = q.emoji ? h('span.q-emoji', { 'aria-hidden': 'true' }, q.emoji) : null;
  const built = renderQuestionBody(q, number, marker, mode, id, a, label, wrap, emoji);
  if (emoji && mode !== 'row') {
    const body = h('div.q-body');
    while (built.firstChild) body.append(built.firstChild);
    built.classList.add('q--emoji');
    built.append(emoji, body);
  }
  return built;
}

function renderQuestionBody(q, number, marker, mode, id, a, label, wrap, emoji) {
  const err = h('p.q-error', { hidden: true, id: id + '-err' });
  const cfg = q.config || {};
  const inst = q.instruction ? h('p.q-inst', q.instruction) : null;
  const style = q.display_style;
  if (mode === 'row') {
    // Zeile einer Matrix-Batterie: Aussage links, Optionen als Raster (ab 640 px), auf dem Smartphone als Karte
    // div statt fieldset/legend: eine Legende nimmt nicht am Grid teil, die Spalten würden nicht unter der Kopfzeile stehen
    const rfs = h('div.matrix-row.matrix-row--battery', { role: 'group', 'aria-labelledby': id + '-lab', 'aria-describedby': id + '-err' }, h('div.matrix-row__label', { id: id + '-lab', class: 'matrix-row__label' + (emoji ? ' matrix-row__label--emoji' : '') }, emoji, h('span.matrix-row__text', ...label, q.instruction ? h('span.q-inst.q-inst--inline', q.instruction) : null)),
      h('div.choices.choices--inline', q.options.map(o => choice('radio', q.question_key, o, a.option === o.option_key, rowStyle(q), true, rowMode(q)))));
    add(wrap, rfs, err);
    return wrap;
  }
  switch (q.question_type) {
    case 'info':
      add(wrap, h('div.q-info.rt', ...richOrText(q.text, 3)), infoMore(q), inst); return wrap;
    case 'divider': {
      // Trennlinie (Fix 3): Stärke 1–4 px, Farbe gedämpft/Themenfarbe, Breite 100/75/50/25 % (zentriert) – per CSSOM
      const hr = h('hr.q-divider', { class: 'q-divider' + (cfg.color === 'theme' ? ' q-divider--theme' : '') });
      hr.style.borderTopWidth = `${Math.min(4, Math.max(1, Number(cfg.thickness) || 1))}px`;
      const w = Number(cfg.width) || 100; if (w !== 100) hr.style.width = `${w}%`;
      wrap.classList.add('q--divider'); add(wrap, hr); return wrap;
    }
    case 'image': {
      // Bild (Fix 3): Datei aus storage/uploads/images, Breite in % der Inhaltsbreite, Ausrichtung; text = Alternativtext
      wrap.classList.add('q--image', `q--image-${cfg.align || 'center'}`);
      if (cfg.file) {
        const img = h('img.q-image', { src: `/media/image/${encodeURIComponent(cfg.file)}` + (cfg.etag ? `?v=${encodeURIComponent(cfg.etag)}` : ''), alt: q.text || '', loading: 'lazy', decoding: 'async' });
        if (cfg.file_width && cfg.file_height) { img.width = Number(cfg.file_width); img.height = Number(cfg.file_height); }
        const w = Number(cfg.width) || 100; if (w !== 100) img.style.width = `${w}%`;
        add(wrap, img);
      } else add(wrap, h('p.q-inst', q.text || ''));
      return wrap;
    }
    case 'personal_code': {
      // Sprint 7f: persönlicher Code – je Teil ein kurzes Feld; gespeichert wird nur ein Schlüssel, daher kommt ein
      // erfasster Code nie zurück („bereits erfasst“: leer lassen = unverändert)
      const parts = (cfg.parts && cfg.parts.length ? cfg.parts : ['mother_first', 'father_first', 'birth_day', 'house_last']);
      if (a.captured || a.keep) wrap.dataset.captured = '1';
      const fs = h('fieldset.q-fieldset.q-code', { 'aria-describedby': id + '-err ' + id + '-hint' }, h('legend.q-label', ...label), inst,
        h('p.q-inst', { id: id + '-hint' }, t('pc_hint')),
        a.captured || a.keep ? h('p.q-inst.q-code__captured', t('pc_captured')) : null,
        h('div.q-code__parts', parts.map(pk => {
          const fmt = PC_FORMAT[pk] || 'L';
          return h('label.q-code__part', h('span.q-code__label', t('pc_' + pk)),
            h('input.q-input.q-code__input', { type: 'text', 'data-part': pk, value: (a.parts && a.parts[pk]) || '', maxlength: fmt === 'D2' ? 2 : 1, inputmode: fmt === 'L' ? 'text' : 'numeric', autocomplete: 'off', autocapitalize: 'characters', spellcheck: false,
              placeholder: fmt === 'D2' ? '07' : fmt === 'D1' ? '3' : 'A', 'aria-describedby': id + '-err' }));
        })),
        h('p.q-inst.q-code__unknown', t('pc_unknown')));
      add(wrap, fs, err); return wrap;
    }
    case 'text_short': case 'email': {
      const keyOnly = cfg.link_anchor && !cfg.link_store_plain;   // Sprint 7f: Anker ohne Klartext
      if (keyOnly && (a.captured || a.keep)) wrap.dataset.captured = '1';
      add(wrap, h('label.q-label', { for: id + '-in' }, ...label), inst, h('input.q-input', { id: id + '-in', type: q.question_type === 'email' ? 'email' : 'text', value: a.text ?? '', maxlength: cfg.max_length || (q.question_type === 'email' ? 254 : 255), placeholder: keyOnly && (a.captured || a.keep) ? t('pc_captured_short') : (cfg.placeholder || ''), inputmode: q.question_type === 'email' ? 'email' : 'text', autocomplete: 'off', 'aria-describedby': id + '-err' }),
        keyOnly ? h('p.q-inst', t('anchor_hint')) : null,
        q.question_type === 'email' && cfg.consent_note ? h('p.q-inst', cfg.consent_note) : null, err); return wrap;
    }
    case 'text_long':
      add(wrap, h('label.q-label', { for: id + '-in' }, ...label), inst, h('textarea.q-input', { id: id + '-in', rows: 4, maxlength: cfg.max_length || 4000, placeholder: cfg.placeholder || '', 'aria-describedby': id + '-err' }, a.text ?? ''), err); return wrap;
    case 'number':
      add(wrap, h('label.q-label', { for: id + '-in' }, ...label), inst, h('div.q-row', h('input.q-input.q-input--short', { id: id + '-in', type: 'number', inputmode: 'decimal', step: cfg.step ?? 'any', min: cfg.min ?? undefined, max: cfg.max ?? undefined, value: a.number ?? '', 'aria-describedby': id + '-err' }), cfg.unit ? h('span.q-unit', cfg.unit) : null), (cfg.min !== undefined || cfg.max !== undefined) ? h('p.q-inst', [cfg.min !== undefined ? `mindestens ${cfg.min}` : null, cfg.max !== undefined ? `höchstens ${cfg.max}` : null].filter(Boolean).join(', ')) : null, err); return wrap;
    case 'date':
      add(wrap, h('label.q-label', { for: id + '-in' }, ...label), inst, h('input.q-input.q-input--short', { id: id + '-in', type: 'date', value: a.date ?? '', min: cfg.min_date || undefined, max: cfg.max_date || undefined, 'aria-describedby': id + '-err' }), err); return wrap;
    case 'single_choice': case 'rating': {
      const fs = h('fieldset.q-fieldset', { 'aria-describedby': id + '-err' }, h('legend.q-label', ...label), inst);
      if (style === 'dropdown') {
        const sel = h('select.q-input', { id: id + '-in', name: q.question_key }, h('option', { value: '' }, t('please_choose')), q.options.map(o => h('option', { value: o.option_key, selected: a.option === o.option_key }, optionText(q, o))));
        add(fs, sel, otherInput(q, a, () => sel.value));
        sel.addEventListener('change', () => toggleOther(fs, q, sel.value));
      } else if (style === 'slider') {
        fs.append(slider(q, a));
      } else {
        fs.append(choiceGroup(q, a, 'radio', style, q.layout));
      }
      add(fs, err); add(wrap, fs); return wrap;
    }
    case 'multi_choice': {
      const fs = h('fieldset.q-fieldset', { 'aria-describedby': id + '-err' }, h('legend.q-label', ...label), inst, (cfg.min_selected || cfg.max_selected) ? h('p.q-inst', [cfg.min_selected ? t('select_min', { n: cfg.min_selected }) : null, cfg.max_selected ? t('select_max', { n: cfg.max_selected }) : null].filter(Boolean).join(', ') + t('select_suffix')) : null, choiceGroup(q, a, 'checkbox', 'checkbox', q.layout), err);
      add(wrap, fs); return wrap;
    }
    case 'matrix_single': {
      if (style === 'dropdown') {
        // Sprint 7a: Auswahlliste je Zeile (platzsparend, z. B. bei vielen Optionen)
        const fs = h('fieldset.q-fieldset.q-matrix.q-matrix--select', { 'aria-describedby': id + '-err' }, h('legend.q-label', ...label), inst);
        for (const r of q.rows) {
          const rowA = (a.rows && a.rows[r.row_key]) || null;
          const sid = `${id}__${r.row_key}`;
          fs.append(h('div.matrix-row.matrix-row--select', { 'data-row': r.row_key },
            h('label.matrix-row__label', { for: sid }, r.label, r.is_required ? h('span.req', { 'aria-hidden': 'true' }, ' ' + marker) : null),
            h('select.q-input', { id: sid }, h('option', { value: '' }, t('please_choose')), q.options.map(o => h('option', { value: o.option_key, selected: !!rowA && rowA.option === o.option_key }, optionText(q, o))))));
        }
        add(fs, err); add(wrap, fs); return wrap;
      }
      const fs = h('fieldset.q-fieldset.q-matrix', { 'aria-describedby': id + '-err', class: 'q-fieldset q-matrix' + (rowsCarryOptions(q) ? ' q-matrix--rows' : '') }, h('legend.q-label', ...label), inst);
      const head = matrixHead(q);
      fs.style.setProperty('--cols', String(q.options.length));
      if (head) fs.append(head);
      for (const r of q.rows) {
        const rowA = (a.rows && a.rows[r.row_key]) || null;
        // Sprint 7a: div statt fieldset/legend – eine Legende nimmt nicht am Raster teil (ab 640 px stünden die Auswahlkreise
        // sonst unter der Aussage statt unter den Spaltenköpfen); Gruppe für Screenreader über role/aria-labelledby
        const rowId = `${id}__${r.row_key}`;
        const rfs = h('div.matrix-row', { role: 'group', 'aria-labelledby': rowId + '-lab', 'data-row': r.row_key }, h('div.matrix-row__label', { id: rowId + '-lab' }, r.label, r.is_required ? h('span.req', { 'aria-hidden': 'true' }, ' ' + marker) : null),
          h('div.choices.choices--inline', q.options.map(o => choice('radio', `${q.question_key}__${r.row_key}`, o, rowA && rowA.option === o.option_key, rowStyle(q), true, rowMode(q)))));
        fs.append(rfs);
      }
      add(fs, err); add(wrap, fs); return wrap;
    }
    case 'matrix_multi': {
      // Sprint 7a: Mehrfachauswahl je Zeile – Kästchen statt Kreisen; exklusive Optionen („nichts davon“) je Zeile
      const fs = h('fieldset.q-fieldset.q-matrix.q-matrix--multi', { 'aria-describedby': id + '-err', class: 'q-fieldset q-matrix q-matrix--multi' + (rowsCarryOptions(q) ? ' q-matrix--rows' : '') }, h('legend.q-label', ...label), inst,
        (cfg.min_selected || cfg.max_selected) ? h('p.q-inst', [cfg.min_selected ? t('select_min', { n: cfg.min_selected }) : null, cfg.max_selected ? t('select_max', { n: cfg.max_selected }) : null].filter(Boolean).join(', ') + t('select_suffix_row')) : null);
      const head = matrixHead(q);
      fs.style.setProperty('--cols', String(q.options.length));
      if (head) fs.append(head);
      for (const r of q.rows) {
        const rowA = (a.rows && a.rows[r.row_key]) || null;
        const selected = rowA && Array.isArray(rowA.options) ? rowA.options : [];
        const group = h('div.choices.choices--inline', q.options.map(o => choice('checkbox', `${q.question_key}__${r.row_key}`, o, selected.includes(o.option_key), rowStyle(q) === 'radio' ? 'checkbox' : rowStyle(q), true, rowMode(q))));
        group.addEventListener('change', (ev) => exclusiveToggle(group, q, ev.target));
        const rowId = `${id}__${r.row_key}`;
        fs.append(h('div.matrix-row.matrix-row--multi', { role: 'group', 'aria-labelledby': rowId + '-lab', 'data-row': r.row_key }, h('div.matrix-row__label', { id: rowId + '-lab' }, r.label, r.is_required ? h('span.req', { 'aria-hidden': 'true' }, ' ' + marker) : null), group));
      }
      add(fs, err); add(wrap, fs); return wrap;
    }
    case 'ranking': {
      // Sprint 7b: Rangfolge durch Antippen in der gewünschten Reihenfolge (Handy und Tastatur); eingeordnete Elemente
      // lassen sich mit Pfeilen verschieben und wieder entfernen. Stand in wrap.dataset.ranking (JSON) für collect().
      const rows = q.rows || [];
      const limit = cfg.rank_count > 0 && cfg.rank_count < rows.length ? Number(cfg.rank_count) : rows.length;
      const byKey = Object.fromEntries(rows.map(r => [r.row_key, r]));
      let chosen = (Array.isArray(a.ranking) ? a.ranking : []).filter(k => byKey[k]).slice(0, limit);
      const fs = h('fieldset.q-fieldset.q-rank', { 'aria-describedby': id + '-err' }, h('legend.q-label', ...label), inst,
        h('p.q-inst', limit < rows.length ? t('rank_hint_top', { n: limit }) : t('rank_hint_all')));
      const box = h('div.rank-box');
      const status = h('p.rank-status', { 'aria-live': 'polite' });
      const paint = (focusKey, focusWhat) => {
        wrap.dataset.ranking = JSON.stringify(chosen);
        clear(box);
        if (chosen.length) {
          box.append(h('ol.rank-chosen', chosen.map((k, i) => h('li.rank-item', { 'data-row': k },
            h('span.rank-no', { 'aria-hidden': 'true' }, String(i + 1)), h('span.rank-text', byKey[k].label),
            h('span.rank-actions',
              h('button.rank-btn', { type: 'button', 'data-act': 'up', disabled: i === 0, 'aria-label': t('rank_up', { t: byKey[k].label }), onClick: () => { [chosen[i - 1], chosen[i]] = [chosen[i], chosen[i - 1]]; paint(k, 'up'); } }, '↑'),
              h('button.rank-btn', { type: 'button', 'data-act': 'down', disabled: i === chosen.length - 1, 'aria-label': t('rank_down', { t: byKey[k].label }), onClick: () => { [chosen[i + 1], chosen[i]] = [chosen[i], chosen[i + 1]]; paint(k, 'down'); } }, '↓'),
              h('button.rank-btn.rank-btn--remove', { type: 'button', 'data-act': 'remove', 'aria-label': t('rank_remove', { t: byKey[k].label }), onClick: () => { chosen.splice(i, 1); paint(k, 'add'); } }, '×'))))));
        }
        const open = rows.filter(r => !chosen.includes(r.row_key));
        if (chosen.length < limit && open.length) {
          box.append(h('p.rank-pool__title', t('rank_pool')), h('div.rank-pool', open.map(r => h('button.rank-add', { type: 'button', 'data-row': r.row_key, 'aria-label': t('rank_add', { t: r.label, n: chosen.length + 1 }),
            onClick: () => { if (chosen.length < limit) { chosen.push(r.row_key); paint(r.row_key, 'chosen'); } } }, h('span.rank-add__plus', { 'aria-hidden': 'true' }, '+'), h('span', r.label)))));
        }
        status.textContent = t('rank_status', { n: chosen.length, m: limit });
        // Fokus nach der Aktion: auf dasselbe Element (oder, falls weg, auf das nächste offene)
        if (focusKey) {
          const sel = focusWhat === 'chosen' || focusWhat === 'up' || focusWhat === 'down' ? `.rank-item[data-row="${CSS.escape(focusKey)}"] [data-act="${focusWhat === 'down' ? 'down' : 'up'}"]:not(:disabled), .rank-item[data-row="${CSS.escape(focusKey)}"] [data-act="remove"]` : `.rank-add[data-row="${CSS.escape(focusKey)}"]`;
          const target = box.querySelector(sel) || box.querySelector('.rank-add, .rank-btn');
          if (target) target.focus();
        }
      };
      paint(null, null);
      add(fs, box, status, err); add(wrap, fs); return wrap;
    }
    case 'matrix_dropdown': {
      // Sprint 7a: Zeilen × Spalten mit Auswahlliste je Zelle. Ab 640 px als Tabelle (Kopfzeile mit Spalten), am Smartphone
      // je Zeile eine Karte mit „Spalte: Liste“ untereinander – dieselben Elemente, nur das Raster ändert sich (CSS)
      const cols = q.columns || [];
      const fs = h('fieldset.q-fieldset.q-grid', { 'aria-describedby': id + '-err' }, h('legend.q-label', ...label), inst);
      fs.style.setProperty('--gcols', String(cols.length));
      fs.append(h('div.q-grid__head', { 'aria-hidden': 'true' }, h('span'), cols.map(c => h('span.q-grid__colhead', c.label, c.is_required ? h('span.req', ' ' + marker) : null))));
      for (const r of q.rows) {
        const rowA = (a.rows && a.rows[r.row_key] && a.rows[r.row_key].cols) || {};
        const rowId = `${id}__${r.row_key}`;
        fs.append(h('div.q-grid__row', { role: 'group', 'aria-labelledby': rowId + '-lab', 'data-row': r.row_key },
          h('span.q-grid__rowlabel', { id: rowId + '-lab' }, r.label, r.is_required ? h('span.req', { 'aria-hidden': 'true' }, ' ' + marker) : null),
          cols.map(c => {
            const sid = `${rowId}__${c.column_key}`;
            const cur = rowA[c.column_key] ? rowA[c.column_key].option : '';
            return h('div.q-grid__cell', { 'data-col': c.column_key },
              h('label.q-grid__celllabel', { for: sid }, c.label),
              h('select.q-input', { id: sid, 'aria-label': `${r.label} – ${c.label}` }, h('option', { value: '' }, t('please_choose')), (c.options || []).map(o => h('option', { value: o.option_key, selected: cur === o.option_key }, o.label))));
          })));
      }
      add(fs, err); add(wrap, fs); return wrap;
    }
    default:
      add(wrap, h('p.error', t('unknown_type', { t: q.question_type }))); return wrap;
  }
}

function choiceGroup(q, a, type, style, layout) {
  const selected = type === 'radio' ? (a.option ? [a.option] : []) : (a.options || []);
  // Anordnung: horizontal (nebeneinander) / vertical (untereinander); „matrix“ außerhalb einer Batterie verhält sich wie horizontal
  const layoutClass = layout === 'vertical' ? ' choices--layout-vertical' : (layout === 'horizontal' || layout === 'matrix' ? ' choices--layout-horizontal' : '');
  const group = h('div.choices', { class: 'choices choices--' + (style || 'radio') + layoutClass + (q.label_mode && q.label_mode !== 'text' ? ' choices--mode-' + q.label_mode : '') }, q.options.map(o => choice(type, q.question_key, o, selected.includes(o.option_key), style, false, q.label_mode)));
  const other = otherInput(q, a, () => { const c = group.querySelector('input:checked'); return type === 'radio' ? (c ? c.value : '') : [...group.querySelectorAll('input:checked')].map(i => i.value); });
  group.addEventListener('change', (ev) => {
    if (type === 'checkbox') {
      const opt = q.options.find(o => o.option_key === ev.target.value);
      if (opt && opt.is_exclusive && ev.target.checked) { for (const i of group.querySelectorAll('input')) if (i !== ev.target) i.checked = false; }
      else if (ev.target.checked) { for (const i of group.querySelectorAll('input')) { const o2 = q.options.find(o => o.option_key === i.value); if (o2 && o2.is_exclusive) i.checked = false; } }
      toggleOther(group.parentNode, q, [...group.querySelectorAll('input:checked')].map(i => i.value));
    } else toggleOther(group.parentNode, q, ev.target.value);
  });
  const wrap = h('div', group, other);
  return wrap;
}

/**
 * Sichtbarer Text einer Antwortoption nach Anzeigeart der Frage (Nachtrag 4o): text = Antwortlabel (übersetzt),
 * emoji = Darstellungswert (fehlt er, das Label), code = Exportcode. Der Antworttext bleibt für Screenreader erhalten.
 */
/**
 * Batterie/Matrix (Nachtrag 4p.1): Antwortoptionen erscheinen entweder als Kopfzeile mit Auswahlkreisen je Zeile ODER
 * direkt in jeder Zeile (Emojis) – nie beides. Zeilen tragen die Optionen selbst, wenn die Anzeigeart „Emojis“ ist und
 * jede Option einen Darstellungswert hat; sonst zeigt die Kopfzeile die Optionen (Text oder Zahlencode).
 */
function rowsCarryOptions(q) {
  return (q.label_mode || 'text') === 'emoji' && q.options.length > 0 && q.options.every(o => !!o.display_value);
}
function matrixHead(q) {
  if (rowsCarryOptions(q)) return null;
  return h('div.matrix-head', { 'aria-hidden': 'true' }, h('span'), q.options.map(o => h('span.matrix-head__opt', { class: 'matrix-head__opt' + (optionText(q, o) !== o.label ? ' matrix-head__opt--glyph' : '') }, ...breakAfterSlash(optionText(q, o)))));
}
/** Darstellung/Anzeigeart einer Zeile: ohne eigene Optionen in der Zeile nur Auswahlkreise (kein Emoji-Visual). */
function rowStyle(q) { return rowsCarryOptions(q) ? q.display_style : (q.display_style === 'emoji' ? 'radio' : q.display_style); }
function rowMode(q) { return rowsCarryOptions(q) ? 'emoji' : ((q.label_mode || 'text') === 'code' ? 'code' : 'text'); }

function optionText(q, o) {
  const mode = q.label_mode || 'text';
  if (mode === 'emoji' && o.display_value) return o.display_value;
  if (mode === 'code' && o.export_code) return o.export_code;
  return o.label;
}

function choice(type, name, o, checked, style, compact, mode = 'text') {
  const id = `c-${name}-${o.option_key}`.replace(/[^a-z0-9_-]/gi, '_');
  const input = h('input', { type, name, id, value: o.option_key, checked });
  // Darstellungswert groß zeigen bei Emoji-Darstellung oder Anzeigeart „emoji“; Sterne bei Sterne-Darstellung
  const emojiShown = (style === 'emoji' || mode === 'emoji') && !!o.display_value;
  const visual = emojiShown ? h('span.choice__visual', { 'aria-hidden': 'true' }, o.display_value) : (style === 'stars' ? h('span.choice__visual', { 'aria-hidden': 'true' }, '★') : null);
  let text;
  if (mode === 'code' && o.export_code) {
    // Code sichtbar, Antworttext nur für Screenreader
    text = [h('span.choice__text.choice__text--code', { 'aria-hidden': 'true' }, o.export_code), h('span.sr-only', o.label)];
  } else if (emojiShown && mode === 'emoji') {
    text = h('span.choice__text.sr-only', o.label);
  } else {
    text = h('span.choice__text', { class: 'choice__text' + (compact && (style === 'emoji') ? ' sr-only-wide' : '') }, o.label);
  }
  return h('label.choice', { class: `choice choice--${style || 'radio'}` + (o.option_kind !== 'regular' && o.option_kind !== 'other' ? ' choice--special' : ''), for: id }, input, visual, text);
}

/** Exklusive Option in einer Kästchengruppe (Mehrfachauswahl-Matrix): schließt die anderen aus und umgekehrt. */
function exclusiveToggle(group, q, target) {
  if (!target || target.type !== 'checkbox') return;
  const opt = q.options.find(o => o.option_key === target.value);
  if (opt && opt.is_exclusive && target.checked) { for (const i of group.querySelectorAll('input')) if (i !== target) i.checked = false; }
  else if (target.checked) { for (const i of group.querySelectorAll('input')) { const o2 = q.options.find(o => o.option_key === i.value); if (o2 && o2.is_exclusive) i.checked = false; } }
}

function otherInput(q, a, current) {
  const hasOther = q.options.some(o => o.has_other_text);
  if (!hasOther) return null;
  const sel = current();
  const active = Array.isArray(sel) ? sel.some(k => q.options.find(o => o.option_key === k)?.has_other_text) : !!q.options.find(o => o.option_key === sel)?.has_other_text;
  return h('div.q-other', { hidden: !active }, h('label', { for: `o-${q.question_key}` }, q.config?.other_text_label || t('other_label')), h('input.q-input', { id: `o-${q.question_key}`, type: 'text', maxlength: 1000, value: a.other_text ?? '' }));
}
function toggleOther(container, q, sel) {
  const box = container.querySelector('.q-other'); if (!box) return;
  const active = Array.isArray(sel) ? sel.some(k => q.options.find(o => o.option_key === k)?.has_other_text) : !!q.options.find(o => o.option_key === sel)?.has_other_text;
  box.hidden = !active; if (active) box.querySelector('input').focus();
}

function slider(q, a) {
  const regular = q.options.filter(o => o.option_kind === 'regular');
  const special = q.options.filter(o => o.option_kind !== 'regular');
  const idx = Math.max(0, regular.findIndex(o => o.option_key === a.option));
  const touched = !!a.option && regular.some(o => o.option_key === a.option);
  const out = h('output.slider__value', { 'aria-live': 'polite' }, touched ? optionText(q, regular[idx]) : t('slider_hint'));
  const range = h('input.slider', { type: 'range', min: 0, max: regular.length - 1, step: 1, value: idx, name: q.question_key, 'data-touched': touched ? '1' : '', 'aria-valuetext': touched ? regular[idx].label : '' });
  range.addEventListener('input', () => { range.dataset.touched = '1'; out.textContent = optionText(q, regular[Number(range.value)]); range.setAttribute('aria-valuetext', regular[Number(range.value)].label); for (const i of wrap.querySelectorAll('.choices input')) i.checked = false; });
  const wrap = h('div.slider-wrap', h('div.slider__labels', h('span', q.config?.left_label || (regular[0] ? optionText(q, regular[0]) : '')), h('span', q.config?.right_label || (regular.length ? optionText(q, regular[regular.length - 1]) : ''))), range, out,
    special.length ? h('div.choices.choices--radio', special.map(o => { const c = choice('radio', q.question_key, o, a.option === o.option_key, 'radio', false, q.label_mode); c.querySelector('input').addEventListener('change', () => { range.dataset.touched = ''; out.textContent = optionText(q, o); }); return c; })) : null);
  return wrap;
}

/** Sprint 7f: Format der Teile des persönlichen Codes (wie Ask\Participation\LinkKeys::PARTS). */
const PC_FORMAT = { mother_first: 'L', father_first: 'L', own_second: 'L', birth_day: 'D2', birth_month: 'D2', house_last: 'D1' };

/** Liest die Antworten der Seite aus dem DOM in `local`. */
function collect(page) {
  for (const q of page.questions) {
    const box = root.querySelector(`[data-q="${CSS.escape(q.question_key)}"]`); if (!box) continue;
    let v = null;
    switch (q.question_type) {
      case 'info': case 'divider': case 'image': continue;
      case 'text_short': case 'text_long': case 'email': { const t = box.querySelector('.q-input').value.trim(); v = t ? { text: t } : (box.dataset.captured ? { keep: true } : null); break; }
      case 'personal_code': {
        const parts = {}; let any = false;
        for (const inp of box.querySelectorAll('.q-code__input')) { const val = inp.value.trim(); if (val) any = true; parts[inp.dataset.part] = val; }
        v = any ? { parts } : (box.dataset.captured ? { keep: true } : null); break;
      }
      case 'number': { const t = box.querySelector('.q-input').value; v = t !== '' ? { number: t } : null; break; }
      case 'date': { const t = box.querySelector('.q-input').value; v = t ? { date: t } : null; break; }
      case 'single_choice': case 'rating': {
        let key = '';
        const sel = box.querySelector('select'); const range = box.querySelector('input[type=range]');
        if (sel) key = sel.value;
        else if (range) { const sp = box.querySelector('.choices input:checked'); const regular = q.options.filter(o => o.option_kind === 'regular'); key = sp ? sp.value : (range.dataset.touched ? regular[Number(range.value)].option_key : ''); }
        else { const c = box.querySelector('input:checked'); key = c ? c.value : ''; }
        const other = box.querySelector('.q-other input');
        v = key ? { option: key, other_text: other && !other.parentNode.hidden ? other.value.trim() : '' } : null; break;
      }
      case 'multi_choice': { const keys = [...box.querySelectorAll('.choices input:checked')].map(i => i.value); const other = box.querySelector('.q-other input'); v = keys.length ? { options: keys, other_text: other && !other.parentNode.hidden ? other.value.trim() : '' } : null; break; }
      case 'matrix_single': { const rows = {}; let any = false; for (const r of q.rows) { const rowEl = box.querySelector(`[data-row="${CSS.escape(r.row_key)}"]`); const sel = rowEl && rowEl.querySelector('select'); const key = sel ? sel.value : (rowEl && rowEl.querySelector('input:checked') ? rowEl.querySelector('input:checked').value : ''); rows[r.row_key] = key ? { option: key } : null; if (key) any = true; } v = any ? { rows } : null; break; }
      case 'ranking': { let list = []; try { list = JSON.parse(box.dataset.ranking || '[]'); } catch (e) { list = []; } v = list.length ? { ranking: list } : null; break; }
      case 'matrix_multi': { const rows = {}; let any = false; for (const r of q.rows) { const keys = [...box.querySelectorAll(`[data-row="${CSS.escape(r.row_key)}"] input:checked`)].map(i => i.value); rows[r.row_key] = keys.length ? { options: keys } : null; if (keys.length) any = true; } v = any ? { rows } : null; break; }
      case 'matrix_dropdown': { const rows = {}; let any = false; for (const r of q.rows) { const cols = {}; let rowAny = false; for (const c of (q.columns || [])) { const sel = box.querySelector(`[data-row="${CSS.escape(r.row_key)}"] [data-col="${CSS.escape(c.column_key)}"] select`); if (sel && sel.value) { cols[c.column_key] = { option: sel.value }; rowAny = true; } } rows[r.row_key] = rowAny ? { cols } : null; if (rowAny) any = true; } v = any ? { rows } : null; break; }
    }
    local[q.question_key] = v;
  }
}

init().catch(e => render(messageScreen(t('h_error'), e.message || t('h_load_failed'), 'error')));
